import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { collection, getDocs } from "firebase/firestore";

/**
 * A reusable card component for displaying a single statistic.
 * @param {object} props - The component props.
 * @param {string} props.title - The title of the statistic (e.g., "Total Users").
 * @param {number | string} props.value - The value of the statistic.
 * @param {JSX.Element} props.icon - An SVG icon element.
 * @param {string} props.colorClass - Tailwind CSS gradient classes for the background.
 */
const StatCard = ({ title, value, icon, colorClass }) => (
  <div className={`bg-gradient-to-br ${colorClass} p-6 rounded-2xl shadow-lg text-white`}>
    <div className="flex items-center">
      <div className="p-3 bg-white/20 rounded-xl mr-4">{icon}</div>
      <div>
        <p className="text-sm font-medium text-white/90">{title}</p>
        <p className="text-4xl font-bold tracking-tight">{value}</p>
      </div>
    </div>
  </div>
);

/**
 * The main Analytics component that fetches and displays user statistics.
 */
function Analytics() {
  // State to hold the calculated statistics
  const [stats, setStats] = useState({ totalUsers: 0, payingUsers: 0 });
  // State to manage the loading status while fetching data
  const [isLoading, setIsLoading] = useState(true);
  // State to hold any potential error messages
  const [error, setError] = useState(null);

  useEffect(() => {
    // This function fetches all user documents and calculates the stats.
    const fetchUserStats = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Get a reference to the "users" collection in Firestore
        const usersCollectionRef = collection(db, "users");
        const querySnapshot = await getDocs(usersCollectionRef);

        const totalUsers = querySnapshot.size;
        let payingUsers = 0;

        // Loop through each user document to check their status
        querySnapshot.forEach((doc) => {
          // *** THIS IS THE CORRECTED LINE ***
          // It checks for a boolean field named `paid` being true, as shown in your screenshot.
          if (doc.data().paid === true) {
            payingUsers++;
          }
        });

        setStats({ totalUsers, payingUsers });
      } catch (err) {
        console.error("Error fetching user stats:", err);
        setError("Failed to load user statistics. Please check the console for details.");
      } finally {
        // Ensure loading is set to false whether the fetch succeeded or failed
        setIsLoading(false);
      }
    };

    fetchUserStats();
  }, []); // The empty dependency array ensures this effect runs only once on mount

  // Render a loading spinner while data is being fetched
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="ml-3 text-gray-600">Fetching user data...</p>
      </div>
    );
  }

  // Render an error message if the fetch failed
  if (error) {
    return (
      <div className="text-center p-8 bg-red-50 text-red-700 rounded-lg border border-red-200">
        <p className="font-semibold">An Error Occurred</p>
        <p>{error}</p>
      </div>
    );
  }

  // Render the analytics dashboard once data is loaded
  return (
    <div>
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">User Overview</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Total Users Card */}
        <StatCard
          title="Total Registered Users"
          value={stats.totalUsers}
          colorClass="from-indigo-500 to-indigo-600"
          icon={
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.653-.124-1.282-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.653.124-1.282.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
        />
        {/* Total Paying Users Card */}
        <StatCard
          title="Total Paying Users"
          value={stats.payingUsers}
          colorClass="from-green-500 to-green-600"
          icon={
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v.01M12 6v-1m0 1H9m3 0h3m-3 10v-1m0 1v1m0-1H9m3 0h3m-6 0a9 9 0 1112 0v-1a9 9 0 01-12 0v1zm12 0a9 9 0 00-12 0v-1a9 9 0 0012 0v1z" />
            </svg>
          }
        />
      </div>
       <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-sm text-gray-600">
          <span className="font-semibold">Note:</span> 'Paying Users' are identified by the field `paid: true` in their user document in Firestore.
        </p>
      </div>
    </div>
  );
}

export default Analytics;
